// A small, dependency-light GSAP scroll-reveal toolkit.
//
// Exposes a global `v-reveal` directive that fades/slides an element (or its
// children, with `.stagger`) into view on scroll. It is deliberately defensive:
//   - honours prefers-reduced-motion (elements are simply left visible),
//   - animates *from* a hidden state *to* the elementʼs natural CSS, so the end
//     state is always the normal, visible layout (nothing stays hidden),
//   - kills its ScrollTrigger on unmount to avoid leaks across route changes,
//   - never throws into the render: any failure clears inline styles so the
//     content shows normally.
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const canAnimate =
  typeof window !== 'undefined' &&
  !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)

// element -> created tween, so we can tear it (and its ScrollTrigger) down.
const tweens = new WeakMap()

function fromVars(modifiers) {
  const d = 28
  const base = { opacity: 0, duration: 0.7, ease: 'power2.out' }
  if (modifiers.left) base.x = -d
  else if (modifiers.right) base.x = d
  else if (modifiers.zoom) base.scale = 0.94
  else base.y = d // default: rise up
  return base
}

const reveal = {
  mounted(el, binding) {
    if (!canAnimate) return // leave fully visible
    const m = binding.modifiers || {}
    const targets = m.stagger ? el.children : el
    try {
      const tween = gsap.from(targets, {
        ...fromVars(m),
        stagger: m.stagger ? 0.09 : 0,
        delay: Number(binding.value) || 0,
        scrollTrigger: { trigger: el, start: 'top 88%', once: true },
      })
      tweens.set(el, tween)
    } catch {
      gsap.set(m.stagger ? el.children : el, { clearProps: 'all' })
    }
  },
  unmounted(el) {
    const tween = tweens.get(el)
    if (tween) {
      if (tween.scrollTrigger) tween.scrollTrigger.kill()
      tween.kill()
      tweens.delete(el)
    }
  },
}

// Recompute trigger positions (call after route changes / async content loads).
export function refreshReveal() {
  if (canAnimate) ScrollTrigger.refresh()
}

export default reveal
